// RisaPanel — the R.I.S.A assistant, an inline right-side column (Claude-artifact
// style) opened by the "Ask Risa" trigger. Ported from the PCS v3 standalone
// prototype's GeniePanel: greeting + insight chips + suggested questions over a
// scrollable stream, a canned-response engine (typing → response card → feedback),
// and a sticky input bar. Pure-presentational + local conversation state; it takes
// no data layer. Pass `context` to retarget it per page:
//   { greeting, insights: [{tone,text}], questions: [{q,tag}], renderers: {q: fn} }
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SparkleIcon, CloseIcon, ArrowRightIcon, SendIcon } from '../../icons/Icons';
import {
  RESPONSE_RENDERERS,
  DEFAULT_POOL,
  DEFAULT_INSIGHTS,
  DEFAULT_GREETING,
} from './risa.content';
import './risa.css';

function TypingIndicator() {
  return (
    <div className="genie-msg genie typing">
      <span className="genie-msg-mark"><SparkleIcon /></span>
      <div className="genie-msg-body">
        <div className="genie-typing-dots" aria-label="Risa is thinking">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}

function Feedback({ fb, onFb }) {
  return (
    <div className="genie-fb">
      <span className="genie-fb-lbl">Was this helpful?</span>
      <button
        type="button" aria-pressed={fb === 'up'}
        className={'genie-fb-btn' + (fb === 'up' ? ' active' : '')}
        onClick={() => onFb(fb === 'up' ? null : 'up')}
      >👍</button>
      <button
        type="button" aria-pressed={fb === 'down'}
        className={'genie-fb-btn' + (fb === 'down' ? ' active' : '')}
        onClick={() => onFb(fb === 'down' ? null : 'down')}
      >👎</button>
      {fb && <span className="genie-fb-thanks">Thanks — noted.</span>}
    </div>
  );
}

Feedback.propTypes = { fb: PropTypes.oneOf(['up', 'down', null]), onFb: PropTypes.func.isRequired };

function ResponseMsg({ q, fb, onFb, renderers }) {
  const renderer = renderers[q];
  return (
    <div className="genie-msg genie animate-in">
      <span className="genie-msg-mark"><SparkleIcon /></span>
      <div className="genie-msg-body">
        {renderer ? renderer() : (
          <div className="genie-resp-card">
            <p className="resp-intro">I don&apos;t have a tailored answer for that yet — but here&apos;s where to look on your dashboard.</p>
            <button className="genie-resp-cta" type="button">Open dashboard <ArrowRightIcon width="12" height="12" /></button>
          </div>
        )}
        <Feedback fb={fb} onFb={onFb} />
      </div>
    </div>
  );
}

ResponseMsg.propTypes = {
  q: PropTypes.string.isRequired,
  fb: PropTypes.oneOf(['up', 'down', null]),
  onFb: PropTypes.func.isRequired,
  renderers: PropTypes.object.isRequired,
};

export function RisaPanel({ onClose, context }) {
  const POOL = (context && context.questions) || DEFAULT_POOL;
  const insights = (context && context.insights) || DEFAULT_INSIGHTS;
  const renderers = context && context.renderers
    ? { ...RESPONSE_RENDERERS, ...context.renderers }
    : RESPONSE_RENDERERS;
  const greetingNode = (context && context.greeting) || DEFAULT_GREETING;

  const [messages, setMessages] = useState([]); // { role: 'user'|'genie', q?, text? }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [askedQs, setAskedQs] = useState([]);
  const [feedback, setFeedback] = useState({});
  const streamRef = useRef(null);
  const inputRef = useRef(null);

  const visibleQs = POOL.filter((p) => !askedQs.includes(p.q)).slice(0, 4);

  const ask = (q) => {
    if (sending) return;
    setAskedQs((arr) => (arr.includes(q) ? arr : [...arr, q]));
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setMessages((m) => [...m, { role: 'genie', q }]);
    }, 950);
  };

  const onSubmit = (e) => {
    e?.preventDefault?.();
    const v = input.trim();
    if (!v || sending) return;
    setInput('');
    // Match a seeded question (case-insensitive); else treat as freeform.
    const match = POOL.find((p) => p.q.toLowerCase() === v.toLowerCase());
    ask(match ? match.q : v);
  };

  // Autoscroll the stream as messages / typing arrive.
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages.length, sending]);

  // Focus the input once the open animation settles.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 280);
    return () => clearTimeout(t);
  }, []);

  return (
    <aside className="genie-panel" aria-label="Risa assistant">
      <header className="genie-head">
        <div className="genie-head-l">
          <span className="genie-mark"><SparkleIcon /></span>
          <div>
            <div className="genie-title">Risa</div>
            <div className="genie-sub">Your retirement copilot</div>
          </div>
        </div>
        <button className="genie-close" onClick={onClose} aria-label="Close Risa" type="button">
          <CloseIcon width="14" height="14" />
        </button>
      </header>

      <div className="genie-stream" ref={streamRef}>
        <div className="genie-greet">{greetingNode}</div>

        <div className="genie-section-h">Insights</div>
        <div className="genie-insights">
          {insights.map((ins, i) => (
            <div key={i} className={'genie-insight ' + ins.tone}>
              <span className="genie-insight-dot"><SparkleIcon /></span>
              <span className="genie-insight-text">{ins.text}</span>
            </div>
          ))}
        </div>

        {visibleQs.length > 0 && (
          <>
            <div className="genie-section-h">Suggested for you</div>
            <div className="genie-questions">
              {visibleQs.map((p) => (
                <button key={p.q} className="genie-question" type="button" onClick={() => ask(p.q)}>
                  <span>{p.q}</span>
                  <ArrowRightIcon width="13" height="13" />
                </button>
              ))}
            </div>
          </>
        )}

        {messages.map((m, i) => (
          m.role === 'user' ? (
            <div key={i} className="genie-msg user animate-in">
              <span className="genie-msg-text">{m.text}</span>
            </div>
          ) : (
            <ResponseMsg
              key={i}
              q={m.q}
              fb={feedback[i] ?? null}
              renderers={renderers}
              onFb={(v) => setFeedback((f) => ({ ...f, [i]: v }))}
            />
          )
        ))}

        {sending && <TypingIndicator />}
      </div>

      <form className="genie-foot" onSubmit={onSubmit}>
        <div className={'genie-input-wrap' + (input ? ' has-text' : '') + (sending ? ' is-disabled' : '')}>
          <input
            ref={inputRef}
            className="genie-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Risa anything..."
            disabled={sending}
            autoComplete="off"
          />
          <button
            className={'genie-send' + (input.trim() && !sending ? ' active' : '')}
            type="submit"
            aria-label="Send"
            disabled={!input.trim() || sending}
          >
            <SendIcon />
          </button>
        </div>
        <div className="genie-foot-hint">Risa is in beta · informational, not financial advice</div>
      </form>
    </aside>
  );
}

RisaPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  /** Optional per-page override: { greeting, insights, questions, renderers }. */
  context: PropTypes.shape({
    greeting: PropTypes.node,
    insights: PropTypes.array,
    questions: PropTypes.array,
    renderers: PropTypes.object,
  }),
};

export default RisaPanel;
